<template>
  <div class="category-selector">
    <div class="selected-categories">
      <div 
        v-for="(cat, index) in value" 
        :key="index" 
        class="category-chip"
      >
        <span class="chip-parent">{{ cat.Parent }}</span>
        <span class="chip-separator">›</span>
        <span class="chip-name">{{ cat.Name }}</span>
        <button @click="removeCategory(index)" class="chip-remove" type="button">
          <fa icon="times" />
        </button>
      </div>
      
      <div v-if="value.length === 0" class="empty-state">
        No categories selected
      </div>
    </div>

    <div class="add-category-section">
      <div class="form-row">
        <div class="form-field">
          <label>Parent Category</label>
          <select v-model="newCategory.Parent" class="category-select">
            <option disabled value="">Select parent...</option>
            <option v-for="parent in existingParents" :key="parent" :value="parent">
              {{ parent }}
            </option>
            <option value="__new__">+ Create New Parent</option>
          </select>
        </div>
        
        <div class="form-field" v-if="newCategory.Parent === '__new__'">
          <label>New Parent Name</label>
          <input 
            v-model="customParent" 
            placeholder="e.g. Desserts" 
            class="category-input"
          />
        </div>
      </div>

      <div class="form-row">
        <div class="form-field flex-grow autocomplete-wrapper">
          <label>Category Name</label>
          <input 
            type="text"
            v-model="newCategory.Name" 
            placeholder="Category Name" 
            @input="showAutocomplete = true"
            @blur="hideAutocomplete"
            @keydown="handleKeyDown"
            class="category-input"
            tabindex="6"
          />
          <div v-if="showAutocomplete && filteredAutocomplete.length" class="autocomplete-dropdown">
            <div 
              v-for="(suggestion, index) in filteredAutocomplete" 
              :key="index"
              @mousedown="selectSuggestion(suggestion)"
              @mouseenter="selectedIndex = index"
              class="autocomplete-item"
              :class="{ 'highlighted': index === selectedIndex }"
            >
              {{ suggestion.Parent }} / {{ suggestion.Name }}
            </div>
          </div>
        </div>
        <div class="form-field align-end">
          <button @click="addCategory" type="button" class="add-btn">
            <fa icon="plus" /> Add
          </button>
        </div>
      </div>

      <!-- Quick suggestions -->
      <div class="suggestions" v-if="suggestedCategories.length > 0">
        <span class="suggestions-label">Suggestions:</span>
        <button 
          v-for="(suggestion, idx) in suggestedCategories.slice(0, 5)" 
          :key="idx"
          @click="addSuggestion(suggestion)"
          type="button"
          class="suggestion-chip"
        >
          {{ suggestion.Parent }} › {{ suggestion.Name }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'CategorySelector',
    props: {
        value: {
            type: Array,
            default: () => []
        },
        recipeContent: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            newCategory: {
                Parent: '',
                Name: ''
            },
            customParent: '',
            showAutocomplete: false,
            selectedIndex: 0
        }
    },
    computed: {
        ...mapGetters(['allRecipes']),
        existingParents() {
            const parents = new Set();
            this.allRecipes.forEach(r => {
                if(r.Categories) {
                    r.Categories.forEach(c => parents.add(c.Parent));
                }
            });
            return Array.from(parents).sort();
        },
        allExistingCategories() {
            const categories = [];
            this.allRecipes.forEach(r => {
                if(r.Categories) {
                    r.Categories.forEach(c => {
                        if (!categories.find(cat => cat.Parent === c.Parent && cat.Name === c.Name)) {
                            categories.push({ Parent: c.Parent, Name: c.Name });
                        }
                    });
                }
            });
            return categories;
        },
        suggestedCategories() {
            // Filter out already selected categories
            let filtered = this.allExistingCategories.filter(cat => 
                !this.value.some(v => v.Parent === cat.Parent && v.Name === cat.Name)
            );

            // If we have recipe content, score based on relevance
            if (this.recipeContent.title || this.recipeContent.instructions) {
                const content = (
                    (this.recipeContent.title || '') + ' ' + 
                    (this.recipeContent.instructions || '')
                ).toLowerCase();

                filtered = filtered.map(cat => {
                    let score = 0;
                    const catName = cat.Name.toLowerCase();
                    const catParent = cat.Parent.toLowerCase();
                    
                    // Check if category name appears in content
                    if (content.includes(catName)) score += 10;
                    if (content.includes(catParent)) score += 5;
                    
                    // Check for partial matches
                    const words = catName.split(' ');
                    words.forEach(word => {
                        if (word.length > 3 && content.includes(word)) score += 2;
                    });
                    
                    return { ...cat, score };
                }).sort((a, b) => b.score - a.score);
            }

            return filtered;
        },
        filteredAutocomplete() {
            if (!this.newCategory.Name || this.newCategory.Name.length < 2) return [];
            const query = this.newCategory.Name.toLowerCase();
            return this.allExistingCategories
                .filter(cat => cat.Name.toLowerCase().includes(query))
                .filter(cat => !this.value.some(v => v.Parent === cat.Parent && v.Name === cat.Name))
                .slice(0, 5);
        }
    },
    methods: {
        addCategory() {
            let parent = this.newCategory.Parent;
            if (parent === '__new__') {
                if (!this.customParent.trim()) {
                    alert('Please enter a parent category name');
                    return;
                }
                parent = this.customParent.trim();
            }
            
            if (!parent || !this.newCategory.Name.trim()) {
                alert('Please fill in both parent and category name');
                return;
            }

            const category = {
                Parent: parent,
                Name: this.newCategory.Name.trim()
            };

            // Avoid duplicates
            if (this.value.some(c => c.Parent === category.Parent && c.Name === category.Name)) {
                alert('This category is already added');
                return;
            }

            this.$emit('input', [...this.value, category]);

            // Reset
            this.newCategory.Name = '';
            if(this.newCategory.Parent === '__new__') {
                this.newCategory.Parent = '';
                this.customParent = '';
            }
        },
        removeCategory(index) {
            const newValue = [...this.value];
            newValue.splice(index, 1);
            this.$emit('input', newValue);
        },
        addSuggestion(suggestion) {
            if (!this.value.some(c => c.Parent === suggestion.Parent && c.Name === suggestion.Name)) {
                this.$emit('input', [...this.value, suggestion]);
            }
        },
        selectSuggestion(suggestion) {
            this.newCategory.Parent = suggestion.Parent;
            this.newCategory.Name = suggestion.Name;
            this.showAutocomplete = false;
            this.selectedIndex = 0;
            this.addCategory(); // Automatically add after selecting from autocomplete
        },
        hideAutocomplete() {
            setTimeout(() => {
                this.showAutocomplete = false;
                this.selectedIndex = 0;
            }, 200);
        },
        handleKeyDown(event) {
            if (!this.showAutocomplete || !this.filteredAutocomplete.length) return;

            switch(event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredAutocomplete.length - 1);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (this.filteredAutocomplete[this.selectedIndex]) {
                        this.selectSuggestion(this.filteredAutocomplete[this.selectedIndex]);
                    }
                    break;
                case 'Escape':
                    this.showAutocomplete = false;
                    this.selectedIndex = 0;
                    break;
            }
        },
    }
}
</script>

<style lang="scss" scoped>
.category-selector {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.selected-categories {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 15px;
    background: var(--lgray);
    border-radius: 8px;
    min-height: 60px;
    align-items: center;

    .empty-state {
        color: #999;
        font-style: italic;
        font-size: 0.9rem;
    }
}

.category-chip {
    display: inline-flex;
    align-items: center;
    background: white;
    border: 2px solid var(--main);
    border-radius: 20px;
    padding: 6px 12px;
    gap: 6px;
    font-size: 0.85rem;
    transition: all 0.2s;

    &:hover {
        border-color: var(--darker);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .chip-parent {
        color: var(--darker);
        font-weight: 600;
    }

    .chip-separator {
        color: var(--main);
        font-weight: 600;
    }

    .chip-name {
        color: var(--dark);
    }

    .chip-remove {
        background: var(--main);
        color: white;
        border: none;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        font-size: 0.7rem;
        transition: all 0.2s;
        margin-left: 4px;

        &:hover {
            background: #e74c3c;
            transform: rotate(90deg);
        }
    }
}

.add-category-section {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.form-row {
    display: flex;
    gap: 10px;
    align-items: flex-end;

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;
    }
}

.form-field {
    display: flex;
    flex-direction: column;
    
    &.flex-grow {
        flex: 1;
    }

    &.align-end {
        justify-content: flex-end;
    }

    label {
        font-size: 0.85rem;
        font-weight: 600;
        color: var(--dark);
        margin-bottom: 6px;
    }
}

.category-select,
.category-input {
    padding: 10px 12px;
    border: 2px solid var(--lgray);
    border-radius: 6px;
    font-family: 'Poppins';
    font-size: 0.9rem;
    background: white;
    color: var(--dark);
    transition: all 0.2s;

    &:focus {
        outline: none;
        border-color: var(--main);
    }
}

.category-select {
    cursor: pointer;
    min-width: 200px;

    @media (max-width: 600px) {
        min-width: 100%;
    }
}

.add-btn {
    background: var(--darker);
    color: white;
    border: none;
    border-radius: 6px;
    padding: 10px 20px;
    font-family: 'Poppins';
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
    white-space: nowrap;

    &:hover {
        background: var(--dark);
    }
}

.suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;

    .suggestions-label {
        font-size: 0.8rem;
        color: #666;
        font-weight: 600;
    }

    .suggestion-chip {
        background: var(--lgray);
        border: 1px solid #ddd;
        color: var(--dark);
        padding: 4px 10px;
        border-radius: 12px;
        font-size: 0.75rem;
        cursor: pointer;
        transition: all 0.2s;
        font-family: 'Poppins';

        &:hover {
            background: var(--main);
            color: white;
            border-color: var(--main);
        }
    }
}

.autocomplete-wrapper {
    position: relative;
}

.autocomplete-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 2px solid var(--main);
    border-top: none;
    border-radius: 0 0 6px 6px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 10;
    max-height: 200px;
    overflow-y: auto;
}

.autocomplete-item {
    padding: 10px 12px;
    cursor: pointer;
    transition: background 0.2s;
    font-size: 0.9rem;

    &:hover {
        background: var(--lgray);
    }

    &.highlighted {
        background: var(--lgray);
    }

    .auto-parent {
        color: var(--darker);
        font-weight: 600;
    }

    .auto-name {
        color: var(--dark);
    }
}
</style>
